import { defineStore, acceptHMRUpdate } from 'pinia'

import { apiClient, apiClientWithoutAuth, apiStrapi } from "@/helpers/api.js"
import { useGlobalStore } from '@/stores/GlobalStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { handleError } from '@/services/errorHandler'
import { CHANNELS_BY_TYPE, JOB_PROGRESS_MESSAGES } from '@/constants'

export const useUserStore = defineStore('UserStore', {
  state: () => {
    return {
      user: null,
      loading: true,
      loggedIn: false,
      // email: '',
      token: '',
      overrideAuth: false,
      loginError: null,
      
      plan: 'Rookie',

      subscriptions: [],
      inventorySettings: {},
      titleTemplates: [],
      descriptionTemplates: [],
      selectedTemplate: {
        name: '',
        content: '',
        default: false,
        id: null
      },
      customFields: [],
      uploadingAvatar: false,
      uploadingBanner: false,
      loadingSubscription: false,
      loadingTemplates: false,
      verificationFail: false,
      verificationLinkExpired: false,
      verificationSuccess: false,
      updatePasswordFail: false,
      updatePasswordSuccess: false,
      emailAlreadyExists: false,
      userCreated: false,
      notFoundEmail: false,

      scanners: [],

      lookupEmailInfo: null,

      promoCodeInfo: {},
      promoCodeError: null,
      promoCodeApplied: false,

      paymentMethod: {},

      changePasswordError: null,
      lookupWinbackInfo: null,

      linkToMarketplaceLoader: false,
      redirectMobileScanUrl: '',
      loginOtpPasswordLoader: false,
      loginOtpPasswordErrorMessage: '',
      otpLoginDone: false,

      tax: {},
      selectedPlanPrice: 0,
      validAddress: true,
    }
  },
  getters: {
    flags() {
      if (!this.user || !this.user.flags) return {} 
      return JSON.parse(this.user.flags)
    },
    importingProgress() {
      if (!this.user) return null
      typeof this.user.import_progress === 'string' && (this.user.import_progress = JSON.parse(this.user.import_progress))
      return this.user.import_progress
    },
    getEntitlements() {
      return this.user?.entitlements || []
    }

  },
  actions: {
    updateUserField(field, value) {
      const configStore = useConfigStore()

      configStore.ignoreConfigUpdate = true

      this.user = { ...this.user, [field]: value }

      const authData = localStorage.getItem("cdp_auth")
      const sessionData = JSON.parse(authData)

      // possible if we get auth from nova login
      if (!sessionData) {
        localStorage.setItem("cdp_auth", JSON.stringify({ user: this.user }))
        return
      }

      sessionData.user = { ...sessionData.user, ...this.user }
      localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
    },

    async checkSession(overrideAuthToken = null) {
      if (overrideAuthToken) {
        try {
          this.token = overrideAuthToken
          const response = await apiClient().get(`/users/me`)
          this.user = response.data
          this.loggedIn = true  
          this.overrideAuth = true

          if (this.user?.import_progress) {
            this.user.import_progress = JSON.parse(this.user.import_progress)
          }

          sessionStorage.setItem('cdp_auth_override_token', overrideAuthToken)
        } catch (e) {
          console.log('checkSession error using override auth', e)
          this.user = null
          this.loggedIn = false
        }
      }

      if (!this.loggedIn || !this.user) {
        let authData = localStorage.getItem("cdp_auth")
        if (authData !== null && authData !== "") {
          try {
            const data = JSON.parse(authData)
            this.token = data.token
            this.user = data.user
            this.loggedIn = true
          } catch (e) {
            console.log('checkSession error', e)
            this.loggedIn = false
          }
        }

        if (this.loggedIn) {
          apiClient().get(`/users/me`).then(response => {
            this.user = response.data
            if (this.user?.import_progress) {
              this.user.import_progress = JSON.parse(this.user.import_progress)
            }
            const authData = {
              token: this.token,
              user: response.data,
            }
            localStorage.setItem("cdp_auth", JSON.stringify(authData))
          })
        }
      }
    },

    async logIn(email, password) {
      this.loginError = null
      let success = false

      try {
        const response = await apiClientWithoutAuth().post('/users/login/', {email, password})
        
        const data = response.data

        if (data?.ok) {
          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          // this.email = data.user.email
          this.loggedIn = true
          success = true
        } else {
          this.loginError = 'Login failed'
        }
      } catch (e) {
        console.error('logIn error', e)
        this.loginError = e.response.data.message
      }
      return success
    },

    async logOut() {
      const globalStore = useGlobalStore()
      const configStore = useConfigStore()

      window.Cello && window.Cello("shutdown")
      globalStore.celloAlreadyBooted = false
      localStorage.removeItem('cdp_auth')
      localStorage.removeItem("collx_user")
      sessionStorage.removeItem('cdp_auth_override_token')
      this.token = ""
      this.loggedIn = false
      configStore.config = {}
    },

    async createUser(body) {
      try {
        const { response, data }  = await apiClient().post('/users', body)

        if (response && response.data.message.includes('already exists')) {
          this.emailAlreadyExists = true
        } else {
          this.emailAlreadyExists = false
          this.userCreated = true
        } 
      } catch (error) {
        console.error('createUser error', error)   
      }
    },

    async verificationResetPassword(token) {
      this.verificationLinkExpired = false

      try {
        const { data, response } = await apiClient().get(`/users/verify-reset-password/${token}`)

        if (data?.ok) {
          this.token = token

          this.verificationFail = false
          this.verificationSuccess = true
        } else {
          this.verificationFail = true

          this.verificationLinkExpired = true
        } 
      } catch (error) {
        this.verificationFail = true
        console.error('verificationResetPassword error', error)

        this.verificationLinkExpired = true
      }
    },

    async updatePassword(body) {
      try {
        const { data } = await apiClientWithoutAuth().post(`/users/update-password`, { ...body, token: this.token })

        if (data?.ok) {
          this.token = data.token

          const { data: userData } = await await apiClient().get(`/users/me`)

          if (userData.import_progress) {
            userData.import_progress = JSON.parse(userData.import_progress)
          }

          const sessionData = {
            token: data.token,
            user: userData,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.user = userData
          this.loggedIn = true

          this.updatePasswordFail = false
          this.updatePasswordSuccess = true
        } else {
          this.updatePasswordFail = true
          throw(new Error('updatePassword'))
        }
      } catch (error) {
        this.updatePasswordFail = true
        console.error('updatePassword error', error)
      }
    },

    async verificationEmail(token) {
      this.verificationLinkExpired = false

      try {
        const { data } = await apiClient().get(`/users/verify-email/${token}`)

        if (data?.ok) {
          localStorage.removeItem("cdp_auth")
          this.token = ""
          this.loggedIn = false

          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          
          this.loggedIn = true

          this.verificationFail = false
          this.verificationSuccess = true

         
        } else {
          this.verificationFail = true

          this.verificationLinkExpired = true
        } 
      } catch (error) {
        this.verificationFail = true
        console.error('verificationEmail error', error)

        this.verificationLinkExpired = true
      }
    },

    async reverificationEmail(email) {
      try {
        const { data } = await apiClient().post(`/users/email-verification`, { email })

        if (data?.ok) {
          this.verificationFail = false
          this.verificationSuccess = true
        } else {
          this.verificationFail = true
        } 
      } catch (error) {
        this.verificationFail = true
        console.error('reverificationEmail error', error)
      }
    },

    async lookupEmail(email) {
      try {
        const { data } = await apiClient().post(`/users/lookup`, {
          email
        })
       
        this.lookupEmailInfo = { ...data }

        return data
      } catch (error) {
        console.error('lookupEmail error', error)
      }
    },

    async resetPassword(body) {
      try {
        this.notFoundEmail = false

        await apiClient().post(`/users/reset-password`, body)
      } catch (error) {
        if (error.response.data.message === 'The email does not exist') {
          this.notFoundEmail = true
        }
        console.error('resetPassword error', error)
      }
    },

    async fetchScanners() {
      try {
        const { data } = await apiStrapi(
          '/scanner-models', 
          {
              populate: '*',
          }
        )

        const mappedScanners = data.map(scanner => {
          return scanner?.attributes?.section
        })

        this.scanners = mappedScanners
      } catch (error) {
        console.error('resetPassword error', error)
      }
    },

    async fetchUser() {
      try {
        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        const { data } = await apiClient().get('/users/me')

        this.user = { ...data }

        if (this.user?.import_progress) {
          this.user.import_progress = JSON.parse(this.user.import_progress)
        }

        if (sessionData) {
          sessionData.user = { ...data }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        }
      } catch (error) {
        console.error('fetchUser error', error)
      }
    },

    async updateUser(body) {
      try {
        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData) || {}

        const { data } = await apiClient().patch('/users/me', body)
        this.user = { ...this.user, ...data, banner_placement: sessionData.user?.banner_placement }

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        console.error('updateUser error', error)
      }
    },

    async deleteUser(body) {
      try {
        const { data } = await apiClient().delete('/users/me', body)
        this.logOut()
      } catch (error) {
        console.error('deleteUser error', error)
      }
    },

    async updateAvatar(avatar) {
      try {
        this.uploadingAvatar = true
        const { data } = await apiClient().post('/users/me/avatar-image', avatar)

        this.user = { ...this.user, ...data  }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        this.uploadingAvatar = false
      } catch (error) {
        console.error('updateAvatar error', error)
        this.uploadingAvatar = false
      }
    },

    async removeAvatar() {
      try {
        const { data } = await apiClient().delete('/users/me/avatar-image')

        this.user = { ...this.user, avatar_image: '' }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        console.error('removeAvatar error', error)
      }
    },

    async fetchInventorySettings() {
      try {
        this.loadingSubscription = true
        const { data } = await apiClient().get('/users/me/subscription')
        
        this.inventorySettings = data
        this.loadingSubscription = false
      } catch (error) {
        this.loadingSubscription = false
        console.error('fetchInventorySettings error', error)
      }
    },

    async updateInventorySettings(body) {
      try {
        const { data } = await apiClient().patch('/users/me/subscription', body)

        this.inventorySettings = data
      } catch (error) {
        console.error('updateInventorySettings error', error)
      }
    },

    async addBanner(banner, placement) {
      try {
        this.uploadingBanner = true
        const { data } = await apiClient().post('/users/me/banner-image', banner)

        this.user = { ...this.user, ...data, banner_placement: placement }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
        this.uploadingBanner = false
      } catch (error) {
        console.error('addBanner error', error)
        this.uploadingBanner = false
      }
    },

    async removeBanner() {
      try {
        const { data } = await apiClient().delete('/users/me/banner-image')

        this.user = { ...this.user, banner_image: ''  }

        const authData = localStorage.getItem("cdp_auth")
        const sessionData = JSON.parse(authData)

        sessionData.user = { ...this.user }
        localStorage.setItem("cdp_auth", JSON.stringify(sessionData))
      } catch (error) {
        console.error('removeBanner error', error)
      }
    },

    async fetchCustomFields() {
      try {
        const { data } = await apiClient().get('/custom-fields')

        this.customFields = data.map(item => ({...item, editing: false}))
      } catch (error) {
        console.error('fetchCustomFields error', error)
      }
    },

    async createCustomField(body) {
      try {
        const { data } = await apiClient().post('/custom-fields', body)

        this.fetchCustomFields()
      } catch (error) {
        console.error('createCustomField error', error)
      }
    },

    async updateCustomField(field) {
      try {
        const { data } = await apiClient().patch(`/custom-fields/${field.id}`, field)

        this.fetchCustomFields()
      } catch (error) {
        console.error('updateCustomField error', error)
      }
    },

    async deleteCustomField(fieldId) {
      try {
        const { data } = await apiClient().delete(`/custom-fields/${fieldId}`)

        this.fetchCustomFields()
      } catch (error) {
        console.error('deleteCustomField error', error)
      }
    },

    async changePassword(body) {
      try {
        const { data, response } = await apiClient().post('/users/me/change-password', body)

        if (response) {
          this.changePasswordError = response.data
        }
      } catch (error) {
        console.error('changePassword error', error)
      }
    },

    async fetchTemplates() {
      try {
        this.loadingTemplates = true
        const { data } = await apiClient().get('/templates')

        const titleTemplates = []
        const descriptionTemplates = []

        for(const template of data) {
          template.type === 'title' && titleTemplates.push(template)
          template.type === 'description' && descriptionTemplates.push(template)
        }

        this.titleTemplates = titleTemplates
        this.descriptionTemplates = descriptionTemplates
        this.loadingTemplates = false
      } catch (error) {
        this.loadingTemplates = false
        console.error('fetchTemplates error', error)
      }
    },

    async createTemplate(body, type) {
      const configStore = useConfigStore()
      try {
        const { data } = await apiClient().post('/templates', body)

        this[type] = [ ...this[type], data]

        configStore.fetchConfigs()

        return data
      } catch (error) {
        console.error('createTemplate error', error)
      }
    },

    async updateTemplate(id, body, key) {
      const configStore = useConfigStore()

      try {
        const { data } = await apiClient().put(`/templates/${id}`, body)

        this[key] = this[key].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          } else {
            acc.push(body)
          }

          return acc
        }, [])

        configStore.fetchConfigs()

        return data
      } catch (error) {
        console.error('updateTemplate error', error)
      }
    },

    async deleteTemplate(id, type) {
      const configStore = useConfigStore()

      try {
        const { data } = await apiClient().delete(`/templates/${id}`)

        this[type] = this[type].reduce((acc, item) => {
          if (item.id !== id) {
            acc.push(item)
          }

          return acc
        }, [])

        configStore.fetchConfigs()
        
      } catch (error) {
        console.error('deleteTemplate error', error)
      }
    },

    async validatePromoCode(code) {
      try {
        const { data } = await apiClient().post(`/promo_codes/validate`, {
          code
        })

        if (data?.ok) {
          this.promoCodeInfo = { ...data.promo_code, message: data.message }

          this.promoCodeApplied = true

          return this.promoCodeInfo
        } else {
          this.promoCodeError = data.message
          this.promoCodeApplied = false
        }
      } catch (error) {
        console.error('validatePromoCode error', error)
        this.promoCodeError = error.response?.data?.message
        this.promoCodeApplied = false
      }
    },

    async captureWinbackCampaign(body) {
      try {
        const { data, response } = await apiClient().post(`/users/capture-campaign`, body)

        if (!data && !response?.data?.ok) {
          this.lookupWinbackInfo = null
        }

        if (data?.ok) {
          this.lookupWinbackInfo = data.user
        }

      } catch (error) {
        console.error('captureWinbackCampaign error', error)
        handleError(error, {
          category: 'api',
          action: 'capture-winback-campaign',
          component: 'UserStore',
        })
      }
    },

    async linkToMarketplace(code, marketplaceName) {
      try {
        this.linkToMarketplaceLoader = true

        const { data } = await apiClient().post(`/users/me/link-${marketplaceName}`, { code })

        if (data?.ok) {
          await this.fetchUser()
          this.linkToMarketplaceLoader = false
        }
      } catch (error) {
        console.error('linkToMarketplace error', error)
        handleError(error, {
          category: 'api',
          action: 'link-to-marketplace',
          component: 'UserStore',
          data: { marketplaceName },
        })
        this.linkToMarketplaceLoader = false
      }
    },

    async unlinkToChannel(type) {
      try {
        const { data } = await apiClient().post(`/users/me/unlink-${type}`)
        if (data?.ok || (!data?.connected && data?.message.includes('Shopify account disconnected successfully'))) {
          await this.fetchUser()
        }
      } catch (error) {
        console.error('unlinkToChannel error', error)
        handleError(error, {
          category: 'api',
          action: 'unlink-to-channel',
          component: 'UserStore',
          data: { type },
        })
      }
    },

    async loginOtpPassword(password) {
      try {
        this.loginOtpPasswordLoader = true
        const { data } = await apiClient().post(`users/otp-login`, {
          password
        })

        if (data?.ok) {
          const sessionData = {
            token: data.token,
            user: data.user,
          }
          localStorage.setItem("cdp_auth", JSON.stringify(sessionData))

          this.token = data.token
          this.user = data.user
          this.loggedIn = true
          this.loginOtpPasswordLoader = false
          this.redirectMobileScanUrl = data.redirect_to

          return  data.redirect_to
        }

        if (data?.message) {
          this.loginOtpPasswordErrorMessage = data.message
        }

        this.loginOtpPasswordLoader = false
      } catch (error) {
        this.loginOtpPasswordLoader = false

        this.loginOtpPasswordErrorMessage = error.response?.data?.message
        console.log('loginOtpPassword error', error)
      }
    },

    async sendEvent(event, payload = {}) {
      try {
        const { data } = await apiClient().post(`users/me/events`, {
          event,
          payload
        })
      } catch (error) {
        console.log('sendEvent error', error)
      }
    },

    async getUCCCodeAndUpdateUser() {
      if (this.user?.cello_referrer_code) return

      try {
        window.CelloAttribution('getUcc').then(async (result) => {
          const ucc = result
          await apiClient().patch('/users/me', { cello_referrer_code: ucc })
          window.CelloAttribution('deleteCookies')
        })
      } catch (error) {
        console.log('getUCCCodeAndUpdateUser error', error)
      }
    },

    async fetchUserTaxInfo(location = {}) {
      try {
        const { data } = await apiClient().post('/users/me/taxes', { price: this.selectedPlanPrice, ...location })

        this.tax = data
      } catch (error) {
        console.error('fetchUserTaxInfo error', error)
      }
    },

    async validationAddress(location) {
      try {
        const { data } = await apiClient().post('/users/me/validate-address', { ...location })

        this.validAddress = data.valid

        return this.validAddress
      } catch (error) {
        console.error('validationAddress error', error)
      }
    },

    async updateUserDailySaleRecapTime(time) {
      try {
        const { data } = await apiClient().post('/users/me/notifications/daily-pick-hour', {
          enabled: true,
          hour: time,
        })
      } catch (error) {
        console.error('updateUserDailySaleRecapTime error', error)
      }
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
